import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Container, Row } from "react-bootstrap";

import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";

import uniqBy from "lodash/uniqBy";
import { IdName } from "../../shared/interfaces/Asset";

import { Media } from "./useMedia";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
    },
    formControl: {
      margin: theme.spacing(1),
    },
    formLabel: {
      color: theme.palette.text.primary,
      "&.Mui-focused": {
        color: theme.palette.text.primary,
      },
    },
  }),
);

interface Props {
  mediaMetaData?: Media[];
  onFilterChange: (filters: Partial<FilterState>) => void;
  filters: FilterState;
  isPartialResult: boolean | undefined;
}

export interface FilterState {
  media: {
    photos: boolean;
    videos: boolean;
  };
  assets: IdName[];
}

interface CheckboxItem {
  name: string;
  id: number;
  checked?: boolean;
}

export function MediaFilters(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const { mediaMetaData, filters, isPartialResult, onFilterChange } = props;

  const defaultAssetCheckboxes = uniqBy(
    mediaMetaData?.map((media) => {
      return {
        name: media.SurveyAssetName,
        id: media.AssetId,
        checked: isPartialResult || filters.assets.some((asset) => !isPartialResult && asset.id === media.AssetId),
      } as CheckboxItem;
    }),
    "name",
  )
    .filter((a: CheckboxItem) => a.name)
    .sort((a: CheckboxItem, b: CheckboxItem) => a.name.localeCompare(b.name));

  const [assetCheckboxes, setAssetCheckboxes] = useState<CheckboxItem[]>(defaultAssetCheckboxes);

  useEffect(() => {
    const checkedAssets = assetCheckboxes
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => {
        return { id: checkbox.id, name: checkbox.name };
      });

    onFilterChange({ assets: checkedAssets });
  }, [assetCheckboxes, onFilterChange]);

  function handleMediaTypeChange(event: React.ChangeEvent<HTMLInputElement>) {
    onFilterChange({
      media: { ...filters.media, [event.target.name]: event.target.checked },
    });
  }

  function handleAssetCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedName = event.target.value;
    const checkboxes = [...assetCheckboxes];

    const selectedCheckbox = checkboxes?.find((checkbox) => checkbox.name.toString() === selectedName);

    if (selectedCheckbox) {
      selectedCheckbox.checked = !selectedCheckbox.checked;
      setAssetCheckboxes(checkboxes);
    }
  }

  return (
    <Container fluid>
      <Row>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel className={classes.formLabel}>
            <Typography variant="subtitle2">{t("common.mediaType")}</Typography>
          </FormLabel>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.media.photos}
                  onChange={handleMediaTypeChange}
                  color="primary"
                  name="photos"
                />
              }
              label={t("common.photos")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.media.videos}
                  onChange={handleMediaTypeChange}
                  color="primary"
                  name="videos"
                />
              }
              label={t("common.videos")}
            />
          </FormGroup>
        </FormControl>
      </Row>
      <Row>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel className={classes.formLabel}>
            <Typography variant="subtitle2">{t("asset.assets")}</Typography>
          </FormLabel>
          <FormGroup>
            {assetCheckboxes &&
              assetCheckboxes.length > 0 &&
              assetCheckboxes?.map((assetCheckbox) => (
                <FormControlLabel
                  key={`checkboxLabel-${assetCheckbox.name}-${assetCheckbox.id}`}
                  label={assetCheckbox?.name}
                  control={
                    <Checkbox
                      key={`checkbox-${assetCheckbox.id}`}
                      checked={assetCheckbox?.checked}
                      onChange={handleAssetCheckboxChange}
                      color="primary"
                      value={assetCheckbox.name}
                    />
                  }
                />
              ))}
          </FormGroup>
        </FormControl>
      </Row>
    </Container>
  );
}
